import React, { useEffect, useMemo, useState } from "react";
import { Search, X, ArrowUpDown, ArrowUp, ArrowDown, Warehouse, ImageIcon, ClipboardCheck, Loader2, AlertTriangle, CheckCircle2 } from "lucide-react";
import { api, money, etTime } from "@/lib/api";
import { toast } from "sonner";

const STATUS_STYLES = {
  Available: "bg-green-100 text-green-700",
  Active: "bg-green-100 text-green-700",
  Reserved: "bg-teal-100 text-teal-700",
  Sold: "bg-red-100 text-red-700",
  Draft: "bg-purple-100 text-purple-700",
  Archived: "bg-gray-300 text-gray-700",
  Deleted: "bg-black/80 text-white",
  Hidden: "bg-gray-200 text-gray-600",
};

const AUDIT_LABELS = {
  photos_reviewed: "Photos Reviewed",
  description_reviewed: "Description Reviewed",
  price_verified: "Price Verified",
  weight_verified: "Weight Verified",
  storage_verified: "Storage Location Verified",
  category_verified: "Category Verified",
  shipping_verified: "Shipping Verified",
  ready_to_sell: "Ready to Sell",
};
const AUDIT_KEYS = Object.keys(AUDIT_LABELS);

const FILTERS = [
  { k: "all", label: "All" },
  { k: "ready", label: "Ready to sell" },
  { k: "needs_review", label: "Needs review" },
  { k: "weight", label: "Weight unverified" },
  { k: "storage", label: "Missing storage" },
  { k: "photos", label: "Missing photos" },
  { k: "category", label: "Missing category" },
  { k: "out_of_stock", label: "Out of stock" },
  { k: "free_ship", label: "Free shipping" },
  { k: "calc_ship", label: "Calculated shipping" },
];

const matchesFilter = (r, f) => {
  const a = r.audit || {};
  switch (f) {
    case "ready": return !!a.ready_to_sell;
    case "needs_review": return r.status !== "Deleted" && (AUDIT_KEYS.some((k) => !a[k]) || (r.issues || []).length > 0);
    case "weight": return r.status !== "Deleted" && !a.weight_verified && !r.free_shipping;
    case "storage": return r.status !== "Deleted" && !(r.storage_location || "").trim();
    case "photos": return r.status !== "Deleted" && (r.images_count || 0) === 0;
    case "category": return r.status !== "Deleted" && !(r.category || "").trim();
    case "out_of_stock": return r.status === "Sold" || r.status === "Reserved";
    case "free_ship": return !!r.free_shipping;
    case "calc_ship": return r.status !== "Deleted" && !r.free_shipping;
    default: return true;
  }
};

const StatCard = ({ label, value, tone, testid }) => {
  const tones = {
    green: "border-green-300 bg-green-50 text-green-800",
    yellow: "border-amber-300 bg-amber-50 text-amber-800",
    red: "border-red-300 bg-red-50 text-red-700",
    navy: "border-navy/20 bg-white text-navy",
  };
  return (
    <div data-testid={testid} className={`rounded-lg border p-3 ${tones[tone]}`}>
      <div className="text-2xl font-black leading-none">{value}</div>
      <div className="text-[11px] font-bold uppercase tracking-wide mt-1 opacity-80">{label}</div>
    </div>
  );
};

function AuditPanel({ row, onSaved, onClose }) {
  const [flags, setFlags] = useState({ ...row.audit });
  const [saving, setSaving] = useState(false);

  const toggle = (k) => setFlags((f) => ({ ...f, [k]: !f[k] }));
  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/products/${row.product_id}`, { audit: flags });
      toast.success(`QC saved for ${row.inventory_id || row.name}`);
      onSaved(flags);
    } catch {
      toast.error("Could not save QC checklist");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div data-testid="audit-panel" onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg w-full max-w-md p-6 max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-xl text-navy">QC — <span className="font-mono text-brick">{row.inventory_id || "no ID"}</span></h2>
          <button onClick={onClose} className="text-navy/50 hover:text-brick"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-sm text-ink/60 mt-1 truncate">{row.name}</p>
        {row.weight_backfilled && !flags.weight_verified && (
          <p data-testid="audit-backfill-warning" className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-2.5 text-xs font-semibold text-amber-800">
            ⚠ Weight was auto-set to 1 lb (16 oz). Confirm or correct the real weight before checking "Weight Verified".
          </p>
        )}
        {(row.issues || []).length > 0 && (
          <ul className="mt-3 rounded-md border border-red-200 bg-red-50 p-2.5 text-xs text-red-700 space-y-1" data-testid="audit-issues">
            {row.issues.map((i) => <li key={i}>• {i}</li>)}
          </ul>
        )}
        <div className="mt-4 space-y-2">
          {AUDIT_KEYS.map((k) => (
            <label key={k} data-testid={`audit-flag-${k}`} className={`flex items-center gap-3 rounded-md border px-3 py-2.5 cursor-pointer ${flags[k] ? "border-green-300 bg-green-50" : "border-navy/15"}`}>
              <input type="checkbox" checked={!!flags[k]} onChange={() => toggle(k)} />
              <span className={`text-sm font-semibold ${flags[k] ? "text-green-800" : "text-navy"}`}>{AUDIT_LABELS[k]}</span>
            </label>
          ))}
        </div>
        <button data-testid="audit-save-btn" onClick={save} disabled={saving}
          className="mt-4 w-full bg-brick text-white font-bold uppercase rounded-md py-3 hover:bg-navy transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
          {saving && <Loader2 className="w-4 h-4 animate-spin" />} Save QC Checklist
        </button>
      </div>
    </div>
  );
}

function AuditReport({ data, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div data-testid="audit-report" onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg w-full max-w-2xl p-6 my-8">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-2xl text-navy">Inventory Audit Report</h2>
          <button onClick={onClose} className="text-navy/50 hover:text-brick"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-sm text-ink/60 mt-1">
          {etTime(data.generated_at)} — {data.total_products} products scanned ·{" "}
          <b className="text-green-700">{data.complete} complete</b> ·{" "}
          <b className="text-brick">{data.incomplete} need work</b>
        </p>
        {data.incomplete === 0 ? (
          <div className="mt-8 text-center text-green-700 font-bold flex flex-col items-center gap-2 pb-6">
            <CheckCircle2 className="w-10 h-10" /> Every product is 100% complete. 🎉
          </div>
        ) : (
          <div className="mt-4 divide-y divide-navy/10">
            {data.report.map((p) => (
              <div key={p.product_id} data-testid={`audit-report-row-${p.inventory_id}`} className="py-3 flex gap-3">
                <div className="w-10 h-10 rounded bg-cream overflow-hidden shrink-0 flex items-center justify-center">
                  {p.image ? <img src={p.image} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="w-4 h-4 text-navy/30" />}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-navy truncate"><span className="font-mono text-brick">{p.inventory_id || "—"}</span> · {p.name}</p>
                  {p.issues.length > 0 && <p className="text-xs text-red-600 mt-0.5">Missing data: {p.issues.join("; ")}</p>}
                  {p.unchecked.length > 0 && <p className="text-xs text-amber-700 mt-0.5">Unchecked QC: {p.unchecked.map((k) => AUDIT_LABELS[k]).join(", ")}</p>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const COLUMNS = [
  { key: "inventory_id", label: "Inventory ID" },
  { key: "name", label: "Product" },
  { key: "category", label: "Category" },
  { key: "stock", label: "Qty" },
  { key: "price", label: "Price" },
  { key: "status", label: "Status" },
  { key: "audit_done", label: "QC" },
  { key: "created_at", label: "Date added" },
  { key: "storage_location", label: "Storage" },
  { key: "orders", label: "Order #" },
];

export default function AdminInventory() {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [sortKey, setSortKey] = useState("inventory_id");
  const [sortDir, setSortDir] = useState(1);
  const [qcRow, setQcRow] = useState(null);
  const [auditData, setAuditData] = useState(null);
  const [auditing, setAuditing] = useState(false);

  const load = () => Promise.all([
    api.get("/admin/inventory").then((r) => setRows(r.data)),
    api.get("/admin/inventory/dashboard").then((r) => setStats(r.data)),
  ]).finally(() => setLoading(false));

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const runAudit = async () => {
    setAuditing(true);
    try {
      const { data } = await api.get("/admin/inventory/audit");
      setAuditData(data);
    } catch {
      toast.error("Audit failed to run");
    } finally {
      setAuditing(false);
    }
  };

  const toggleSort = (key) => {
    if (key === "orders") return;
    if (sortKey === key) setSortDir((d) => -d);
    else { setSortKey(key); setSortDir(1); }
  };

  const visible = useMemo(() => {
    const term = q.trim().toLowerCase();
    let list = rows.filter((r) => matchesFilter(r, filter));
    if (term) {
      list = list.filter((r) =>
        [r.inventory_id, r.name, r.category, r.status, r.storage_location, ...(r.order_numbers || [])]
          .some((f) => (f || "").toLowerCase().includes(term)));
    }
    const val = (r) => {
      const v = r[sortKey];
      if (["price", "stock", "audit_done"].includes(sortKey)) return Number(v ?? -1);
      return (v || "").toString().toLowerCase();
    };
    return [...list].sort((a, b) => (val(a) > val(b) ? sortDir : val(a) < val(b) ? -sortDir : 0));
  }, [rows, q, filter, sortKey, sortDir]);

  const tone = (n, warnAt = 1) => (n === 0 ? "green" : n < warnAt ? "yellow" : "red");

  return (
    <div data-testid="admin-inventory">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Warehouse className="w-8 h-8 text-brick" />
          <div>
            <h1 className="font-display text-4xl text-navy">Inventory</h1>
            <p className="text-ink/60 mt-1">Permanent JM IDs, storage locations and QC — get every item to 100% before it sells.</p>
          </div>
        </div>
        <button data-testid="run-audit-btn" onClick={runAudit} disabled={auditing}
          className="inline-flex items-center gap-2 bg-brick text-white font-bold uppercase rounded-md px-5 py-3 hover:bg-navy transition-colors disabled:opacity-60">
          {auditing ? <Loader2 className="w-4 h-4 animate-spin" /> : <ClipboardCheck className="w-4 h-4" />} Run Inventory Audit
        </button>
      </div>

      {/* Dashboard */}
      {stats && (
        <div data-testid="inventory-dashboard" className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <StatCard testid="stat-total" label="Total products" value={stats.total_products} tone="navy" />
          <StatCard testid="stat-active" label="Active" value={stats.active_products} tone="green" />
          <StatCard testid="stat-oos" label="Out of stock" value={stats.out_of_stock} tone={stats.out_of_stock === 0 ? "green" : "yellow"} />
          <StatCard testid="stat-review" label="Needing review" value={stats.needing_review} tone={tone(stats.needing_review, 10)} />
          <StatCard testid="stat-ready" label="Ready to sell" value={stats.ready_to_sell} tone={stats.ready_to_sell > 0 ? "green" : "yellow"} />
          <StatCard testid="stat-photos" label="Missing photos" value={stats.missing_photos} tone={tone(stats.missing_photos, 3)} />
          <StatCard testid="stat-weight" label="Weight unverified" value={stats.weight_unverified} tone={tone(stats.weight_unverified, 10)} />
          <StatCard testid="stat-backfilled" label="1 lb auto-weights" value={stats.weight_backfilled} tone={stats.weight_backfilled === 0 ? "green" : "red"} />
          <StatCard testid="stat-storage" label="Missing storage" value={stats.missing_storage} tone={tone(stats.missing_storage, 10)} />
          <StatCard testid="stat-category" label="Missing category" value={stats.missing_category} tone={tone(stats.missing_category, 3)} />
          <StatCard testid="stat-freeship" label="Free shipping" value={stats.free_shipping} tone="navy" />
          <StatCard testid="stat-calcship" label="Calculated shipping" value={stats.calculated_shipping} tone="navy" />
          <div data-testid="stat-value" className="rounded-lg border border-gold/50 bg-gold/10 p-3 col-span-2">
            <div className="text-2xl font-black leading-none text-navy">{money(stats.value_retail)}</div>
            <div className="text-[11px] font-bold uppercase tracking-wide mt-1 text-navy/70">
              Est. retail value{stats.value_cost > 0 ? ` · cost ${money(stats.value_cost)}` : ""}
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div data-testid="inventory-filters" className="mt-6 flex gap-2 flex-wrap">
        {FILTERS.map((f) => (
          <button key={f.k} data-testid={`inventory-filter-${f.k}`} onClick={() => setFilter(f.k)}
            className={`rounded-full px-3.5 py-1.5 text-xs font-bold transition-colors ${filter === f.k ? "bg-brick text-white" : "bg-white border border-navy/20 text-navy hover:border-brick"}`}>
            {f.label}
          </button>
        ))}
      </div>

      <div className="mt-4 max-w-md relative">
        <Search className="w-4 h-4 text-navy/40 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
        <input
          data-testid="inventory-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by ID, name, category, status, storage…"
          className="w-full rounded-md border border-navy/20 bg-white pl-9 pr-9 py-2.5 outline-none focus:border-brick"
        />
        {q && (
          <button data-testid="inventory-search-clear" onClick={() => setQ("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-navy/50 hover:text-brick p-1">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      <p className="mt-3 text-sm text-ink/60" data-testid="inventory-count">
        {loading ? "Loading…" : `${visible.length} item${visible.length === 1 ? "" : "s"}`}
      </p>

      <div className="mt-2 bg-white rounded-lg border border-navy/15 overflow-x-auto">
        <table className="w-full text-sm whitespace-nowrap">
          <thead className="bg-navy text-cream text-left">
            <tr>
              {COLUMNS.map((c) => (
                <th key={c.key} className="px-4 py-3">
                  <button data-testid={`inventory-sort-${c.key}`} onClick={() => toggleSort(c.key)}
                    className={`inline-flex items-center gap-1 font-bold ${c.key === "orders" ? "cursor-default" : "hover:text-gold"}`}>
                    {c.label}
                    {c.key !== "orders" && (sortKey === c.key
                      ? (sortDir === 1 ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)
                      : <ArrowUpDown className="w-3 h-3 opacity-40" />)}
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-navy/10">
            {visible.map((r, i) => (
              <tr key={`${r.inventory_id || r.product_id}-${i}`} data-testid={`inventory-row-${r.inventory_id || i}`} className="hover:bg-cream/50">
                <td className="px-4 py-3 font-mono font-bold text-navy">
                  {r.inventory_id || "—"}
                  {r.weight_backfilled && !r.audit?.weight_verified && (
                    <AlertTriangle className="inline w-3.5 h-3.5 text-amber-500 ml-1" title="Weight needs verification" />
                  )}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3 max-w-[240px]">
                    <div className="w-10 h-10 rounded bg-cream overflow-hidden shrink-0 flex items-center justify-center">
                      {r.image ? <img src={r.image} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="w-4 h-4 text-navy/30" />}
                    </div>
                    <span className="font-semibold text-navy truncate">{r.name}</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-ink/70">{r.category}</td>
                <td className="px-4 py-3">{r.stock}</td>
                <td className="px-4 py-3 font-semibold text-brick">{r.price != null ? money(r.price) : "—"}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs font-bold rounded-full px-2 py-1 ${STATUS_STYLES[r.status] || "bg-gray-200 text-gray-600"}`}>{r.status}</span>
                </td>
                <td className="px-4 py-3">
                  {r.status === "Deleted" ? <span className="text-ink/40 text-xs">—</span> : (
                    <button data-testid={`qc-btn-${r.inventory_id}`} onClick={() => setQcRow(r)}
                      className={`text-xs font-bold rounded-full px-2.5 py-1 border transition-colors ${r.audit_done === 8 ? "border-green-300 bg-green-50 text-green-700" : r.audit_done > 0 ? "border-amber-300 bg-amber-50 text-amber-700" : "border-red-200 bg-red-50 text-red-600"}`}>
                      {r.audit_done}/8
                    </button>
                  )}
                </td>
                <td className="px-4 py-3 text-ink/60 text-xs">{r.created_at ? etTime(r.created_at, false) : "—"}</td>
                <td className="px-4 py-3 text-ink/70">{r.storage_location || "—"}</td>
                <td className="px-4 py-3 text-xs font-mono text-navy">{(r.order_numbers || []).join(", ") || "—"}</td>
              </tr>
            ))}
            {!loading && visible.length === 0 && (
              <tr><td colSpan={COLUMNS.length} className="px-4 py-10 text-center text-navy/50" data-testid="inventory-empty">
                {q || filter !== "all" ? "No inventory matches this filter/search." : "No personal inventory items yet."}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {qcRow && (
        <AuditPanel
          row={qcRow}
          onClose={() => setQcRow(null)}
          onSaved={() => { setQcRow(null); load(); }}
        />
      )}
      {auditData && <AuditReport data={auditData} onClose={() => setAuditData(null)} />}
    </div>
  );
}
